
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/components/ui/use-toast';
import { Journalist, JournalistProfileData } from './types';
import { fetchJournalistMetrics, parseJsonArray, checkFollowStatus } from './utils/journalistUtils';

// This hook handles the core journalist profile data fetching
export const useJournalistProfileData = (): JournalistProfileData & {
  setJournalist: React.Dispatch<React.SetStateAction<Journalist | null>>;
  setIsFollowing: React.Dispatch<React.SetStateAction<boolean>>;
} => {
  const { userId } = useParams<{ userId: string }>();
  const { user } = useAuth();
  const [journalist, setJournalist] = useState<Journalist | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);

  useEffect(() => {
    const fetchJournalistProfile = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        if (!userId) {
          throw new Error('Journalist ID is required');
        }
        
        console.log('Fetching profile for journalist ID:', userId);
        
        // Check if this is the user's own profile
        if (user?.id === userId) {
          setIsOwnProfile(true);
          console.log('This is the user\'s own profile');
        }
        
        // Fetch the journalist profile from the profiles table
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();
          
        if (profileError) {
          console.error('Error fetching profile:', profileError);
          throw profileError;
        }
        
        if (!profileData) {
          console.error('No profile data found');
          throw new Error('Journalist not found');
        }
        
        console.log('Profile data fetched:', profileData);
        
        // Get article count, follower count, and other metrics
        const { articleCount, followerCount, yearsActive } = await fetchJournalistMetrics(userId);
        
        // Parse credentials and expertise
        const credentials = parseJsonArray(profileData.credentials);
        const expertise = parseJsonArray(profileData.expertise);
        
        // Construct journalist object from profile data
        const journalistData: Journalist = {
          id: profileData.id,
          name: profileData.display_name || 'Unknown Journalist',
          role: profileData.user_role === 'journalist' ? 'Journalist' : 'Senior Correspondent',
          bio: profileData.bio || 'No biography available.',
          avatar: profileData.avatar_url || 'https://i.pravatar.cc/300?img=12',
          banner: profileData.banner_url || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?q=80&w=1470&h=400&auto=format&fit=crop',
          brandColor: profileData.brand_color || '#9b87f5',
          credentials,
          expertise,
          social: {
            twitter: profileData.twitter || '',
            linkedin: profileData.linkedin || '',
            facebook: profileData.facebook || '',
            instagram: profileData.instagram || '',
            website: profileData.website || '',
          },
          metrics: {
            followers: followerCount || 0,
            articles: articleCount || 0,
            yearsActive
          },
          subscription: {
            price: profileData.subscription_price || 5.99,
            subscribers: profileData.subscriber_count || 0
          }
        };
        
        console.log('Journalist data constructed:', journalistData);
        setJournalist(journalistData);

        // Check follow status if user is logged in
        if (user && userId && user.id !== userId) {
          const isFollowingStatus = await checkFollowStatus(user.id, userId);
          setIsFollowing(isFollowingStatus);
        }
      } catch (error) {
        console.error('Error fetching journalist profile:', error);
        setError(error instanceof Error ? error : new Error('Failed to load journalist profile'));
        toast({
          variant: 'destructive',
          title: 'Error loading profile',
          description: 'There was a problem loading the journalist profile.'
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchJournalistProfile();
  }, [userId, user]);

  return {
    journalist,
    setJournalist,
    isLoading,
    error,
    isOwnProfile,
    isFollowing,
    setIsFollowing
  };
};
